/**
 * Device Detection Utilities
 * Detects mobile devices based on viewport size and touch capability.
 */

// Breakpoint for mobile detection (matches architecture spec)
const MOBILE_BREAKPOINT = 768;

// One MediaQueryList per query: `.matches` stays live, and creating one
// is what cost (the Mindmap asked once per node: 70 ms at 27,000 nodes).
const mediaLists = new Map();
function media(query) {
  let list = mediaLists.get(query);
  if (!list) mediaLists.set(query, (list = window.matchMedia(query)));
  return list;
}

/**
 * The media query mobile.css uses to reveal the mobile shell and hide the
 * desktop chrome. Kept here so code that needs to know "is the desktop UI on
 * screen right now" asks the same question the stylesheet does.
 *
 * Must stay in sync with the top-level @media in interfaces/classic/styles/mobile.css.
 */
export const MOBILE_SHELL_MEDIA_QUERY =
  "(max-width: 767px), (pointer: coarse) and (max-width: 1023px)";

/**
 * True when the mobile shell is genuinely on screen.
 *
 * Both halves are needed. `mobile-shell-active` only says a shell has been
 * rendered into #mobile-app — MobileApp.init() runs unconditionally, so the
 * class is present on a wide desktop window too, where the media query leaves
 * the desktop UI visible and the shell hidden. Testing the class alone would
 * conclude "mobile" on a desktop and skip work the user can see.
 *
 * @returns {boolean}
 */
export function isMobileShellVisible() {
  if (typeof document === "undefined" || typeof window === "undefined") return false;
  if (!document.body?.classList.contains("mobile-shell-active")) return false;
  return media(MOBILE_SHELL_MEDIA_QUERY).matches;
}

/**
 * Check if the current device should use mobile UI.
 * Uses viewport width and touch capability as primary indicators.
 * @returns {boolean} True if mobile UI should be used
 */
export function isMobile() {
  // Check viewport width
  const isNarrowViewport = window.innerWidth < MOBILE_BREAKPOINT;

  // Check for touch as primary input
  const isTouchPrimary = media('(pointer: coarse)').matches;

  // Check for touch capability (backup)
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Mobile if narrow viewport OR touch is primary input method
  return isNarrowViewport || isTouchPrimary;
}

/**
 * Check if the device supports touch input (regardless of primary input).
 * @returns {boolean} True if touch is supported
 */
export function hasTouch() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Check if the device is a tablet (touch + larger screen).
 * Useful for potential future tablet-optimized layouts.
 * @returns {boolean} True if device appears to be a tablet
 */
export function isTablet() {
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isLargeScreen = window.innerWidth >= MOBILE_BREAKPOINT && window.innerWidth < 1024;
  return hasTouch && isLargeScreen;
}

/**
 * Get the current device type.
 * @returns {'mobile' | 'tablet' | 'desktop'} Device type string
 */
export function getDeviceType() {
  if (isMobile()) return 'mobile';
  if (isTablet()) return 'tablet';
  return 'desktop';
}

/**
 * Listen for device type changes (e.g., window resize, orientation change).
 * @param {function(boolean): void} callback - Called with isMobile result when device type may have changed
 * @returns {function(): void} Cleanup function to remove listeners
 */
export function onDeviceChange(callback) {
  const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px), (pointer: coarse)`);

  const handler = () => {
    callback(isMobile());
  };

  // Use addEventListener for modern browsers, addListener for older ones
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handler);
  } else {
    mediaQuery.addListener(handler);
  }

  // Also listen for orientation changes
  window.addEventListener('orientationchange', handler);

  // Return cleanup function
  return () => {
    if (mediaQuery.removeEventListener) {
      mediaQuery.removeEventListener('change', handler);
    } else {
      mediaQuery.removeListener(handler);
    }
    window.removeEventListener('orientationchange', handler);
  };
}
